import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'

import { httpService } from '@/api/v1/http.service'
import type { DeepCamelCase } from '@/types/utils'
import { idAndNameSchema, requiredString } from '@/utils/zod'

type APIResponse = z.infer<typeof responseSchema>
export type Course = DeepCamelCase<APIResponse>

const responseSchema = z.object({
  id: requiredString(),
  name: requiredString(),
  display_name: requiredString(),
  education_level: idAndNameSchema,
  domain: idAndNameSchema,
  preferred_rooms: z.array(idAndNameSchema).nullable(),
  can_be_deleted: z.boolean(),
  curriculums: z
    .array(
      idAndNameSchema.extend({
        semesters: z.array(z.number())
      })
    )
    .nullable()
})

const parseResponse = (response: APIResponse): Course => ({
  id: response.id,
  name: response.name,
  displayName: response.display_name,
  educationLevel: response.education_level,
  domain: response.domain,
  preferredRooms: response.preferred_rooms,
  canBeDeleted: response.can_be_deleted,
  curriculums: response.curriculums
})

export const getCourseQueryOptions = (courseId: string) => ({
  queryKey: ['panelCoursesManagementRetrieve', courseId],
  staleTime: 60 * 1000,
  queryFn: async () => {
    const response = await httpService.panel.panelCoursesRetrieve(courseId, {
      fetchKeys: {
        id: true,
        name: true,
        display_name: true,
        education_level: true,
        domain: true,
        preferred_rooms: true,
        can_be_deleted: true,
        curriculums: true
      }
    })

    return parseResponse(responseSchema.parse(response))
  }
})

export const useCourse = (courseId: string) =>
  useQuery(getCourseQueryOptions(courseId))
